import {
  BracketGraph,
  getBracketGraphMatchTitle,
  isBracketNode,
} from "model/Bracket/BracketGraph";
import { BracketNodeBase } from "model/Bracket/BracketGraphBase";
import { BracketMatch, isSeedSource } from "model/Bracket/BracketMatch";
import {
  getShuffleBracketGraphMatchTitle,
  isShuffleBracketGraph,
  isShuffleBracketNode,
  ShuffleBracketGraph,
} from "model/Bracket/ShuffleBracketGraph";
import { nonUndefined, onlyUnique } from "Utility";
import { TournamentSegmentBase } from "../TournamentSegment";
import { TournamentSegmentGroupBase } from "../TournamentSegmentGroup";

const bracketTypes = [
  "SingleElim",
  "DoubleElim",
  "SingleElimShuffle",
  "DoubleElimShuffle",
  "Multilevel",
] as const;

export type BracketType = (typeof bracketTypes)[number];
export function isBracketType(value: string | undefined): value is BracketType {
  return bracketTypes.includes(value as BracketType);
}

export type BracketSegment = TournamentSegmentBase<"Bracket", Bracket>;

export type Bracket = TournamentSegmentGroupBase & {
  bracketType: BracketType;
  graph: BracketGraph | ShuffleBracketGraph | undefined;
  matches: BracketMatch[];
  name: string | undefined;
};

export const getBracketMatchIds = (bracket: Bracket) => {
  return bracket.matches.flatMap((m) => m.matchId);
};

export const bracketIncludesMatchId = (bracket: Bracket, matchId: string) => {
  return getBracketMatchIds(bracket).includes(matchId);
};

// todo - having to pass in segmentName like this kinda weird?
export const getBracketMatchTitle = (
  bracket: Bracket,
  matchId: string,
  segmentName: string | undefined,
  includeMatchPart: boolean
) => {
  if (!bracketIncludesMatchId(bracket, matchId)) {
    return undefined;
  }

  if (!includeMatchPart) {
    return bracket.name;
  }

  if (!bracket.graph) {
    return `${segmentName}${!!segmentName && !!bracket.name ? " - " : ""}${
      bracket.name
    }`;
  }

  if (!includeMatchPart) {
    return;
  }

  if (isShuffleBracketGraph(bracket.graph)) {
    return `${getShuffleBracketGraphMatchTitle(
      bracket.graph,
      bracket.name,
      matchId
    )}`;
  }

  return `${getBracketGraphMatchTitle(
    bracket.graph,
    bracket.bracketType,
    bracket.name,
    matchId
  )}`;
};

// todo - combine both BracketNode types to use the Shuffle variant
export const getNodeSourceNodes = (
  node: BracketNodeBase
): BracketNodeBase[] | undefined => {
  if (isShuffleBracketNode(node)) {
    return nonUndefined(node.teamSources.map((s) => s.node)).filter(onlyUnique);
  }

  if (isBracketNode(node)) {
    return nonUndefined([node.teamOneSource, node.teamTwoSource]);
  }
};

const getMatchSources = (match: BracketMatch): string[] => {
  if (match.teamSources.length > 0) {
    return match.teamSources.map((s) => s.source);
  }

  return nonUndefined([match.teamOneSource, match.teamTwoSource]);
};

export const getBracketNumberOfTeams = (bracket: Bracket) => {
  return bracket.matches
    .flatMap((m) => getMatchSources(m))
    .filter((s) => isSeedSource(s)).length;
};
